<template>
  <div>
    <v-form v-model="form.valid" class="mt-4" ref="form">
      <website-select
          :filled="true"
          :show-new="true"
          :value.sync="form.fields.website">
      </website-select>
      <v-textarea
          filled
          :prepend-icon="$vuetify.breakpoint.smAndUp ? 'mdi-text-search' : ''"
          v-model="form.fields.keywords"
          :rules="form.rules.keywords"
          :label="$t('label.keywords')"
          :hint="$t('hint.keywords') + maxKeywords + $t('hint.keywords-account')"
      ></v-textarea>
      <v-row>
        <v-col>
          <country-select :filled="true" :value.sync="form.fields.country"></country-select>
        </v-col>
        <v-col>
          <language-select :filled="true" :value.sync="form.fields.language"></language-select>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <tags-select :filled="true" :value.sync="form.fields.tags"></tags-select>
        </v-col>
      </v-row>
    </v-form>
  </div>
</template>

<script>
import moment from 'moment'
import WebsiteSelect from '@/views/Common/Components/Selects/WebsiteSelect'
import CountrySelect from '@/views/Common/Components/Selects/CountrySelect'
import LanguageSelect from '@/views/Common/Components/Selects/LanguageSelect'
import TagsSelect from '@/views/Common/Components/Selects/TagsSelect'

export default {
  name: 'WriteForm',
  components: { TagsSelect, LanguageSelect, CountrySelect, WebsiteSelect },
  data () {
    return {
      form: {
        valid: false,
        fields: {
          website: '',
          keywords: '',
          country: 'en',
          language: 'en',
          tags: [],
        },
        rules: {
          website: [v => !!v || this.$t('required.domain')],
          keywords: [
            v => !!v || this.$t('required.keyword')
          ],
        }
      }
    }
  },
  watch: {
    'form.valid' (valid) {
      this.$emit('valid', valid)
    }
  },
  computed: {
    maxKeywords () {
      return this.$account.current.rules['check-position'].max_monitor
    },
    keywordsList () {
      return Array.from(new Set(this.form.fields.keywords.split('\n')
          .map(term => this.$models.Input.sanitizeKeywordName(term))
          .filter(term => term !== '')
      ))
    }
  },
  methods: {
    sanitizeWebsite () {
      this.form.fields.website = this.$models.Input.sanitizeWebsiteDomain(this.form.fields.website)
    },
    sanitizeKeywords () {
      this.form.fields.keywords = this.form.fields.keywords.split('\n')
          .map(keyword => this.$models.Input.sanitizeKeywordName(keyword, true))
          .join('\n')
    },
    getPayload () {
      return {
        website: this.form.fields.website,
        keywords: this.keywordsList,
        country: this.form.fields.country,
        language: this.form.fields.language,
        tags: this.form.fields.tags,
      }
    },
    async fetch () {
      this.form.fields.country = this.$account.current.country
      this.form.fields.language = this.$account.current.language
    },
    async submit () { // TOOO: it seems that this method is never called, do we need it?
      this.loading = this.$t('add')
      if (this.$refs.form.validate()) {
        this.$autocompletion.add('domains', this.form.fields.website)

        try {
          await this.$repos.monitors.create({
            website: this.form.fields.website,
            keywords: this.keywordsList,
            country: this.form.fields.country,
            language: this.form.fields.language,
            tags: this.form.fields.tags,
          })

          this.$ui.success(this.$t('success'))
          this.$bus.emit('monitoring/require_refresh')
          this.$bus.emit('monitoring/change_date', {
            current: moment().format('YYYY-MM-DD 00:00'),
            comparison: moment().subtract(1, 'day').format('YYYY-MM-DD 00:00')
          })
          if (this.$refs.dialog) this.$refs.dialog.close()
        } catch (err) {
          if (err.exception === 'ThrottledCreateMonitorException') {
            this.$bus.emit('notification/payment_required_for_more_monitors')
          }
        } finally {
          this.loading = ''
        }
      }
    }
  }
}
</script>

<style scoped>

</style>

<i18n>
{
	"en": {
		"add": "Adding keywords ...",
		"hint": {
			"keywords": "Enter a keyword per line, you can import up to ",
			"keywords-account": " keywords on your account."
		},
		"label": {
			"keywords": "Keywords (one per line)",
			"language": "Language"
		},
		"required": {
			"keyword": "At least one keyword is required"
		},
		"success": "Keywords have been added, positions are being updated. "
	},
	"es": {
		"add": "Añadiendo palabras clave ...",
		"hint": {
			"keywords": "Ingrese una palabra clave por línea, puede importar hasta ",
			"keywords-account": " palabras clave en su cuenta."
		},
		"label": {
			"keywords": "Palabras clave (una por línea)",
			"language": "Lengua"
		},
		"required": {
			"keyword": "Se requiere al menos una palabra clave"
		},
		"success": "Se han agregado palabras clave, las posiciones se están actualizando."
	},
	"fr": {
		"add": "Ajout des mots clés ...",
		"hint": {
			"keywords": "Saisissez un mot clé par ligne, vous pouvez importer jusqu'à ",
			"keywords-account": " mots clés sur votre compte."
		},
		"label": {
			"keywords": "Mots-clés (un par ligne)",
			"language": "Langue"
		},
		"required": {
			"keyword": "Au moins un mot-clé est requis"
		},
		"success": "Les mots clés ont été ajoutés, les positions sont en cours d'actualisation."
	},
	"it": {
		"add": "Aggiunta parole chiave ...",
		"hint": {
			"keywords": "Inserisci una parola chiave per riga, puoi importarne fino a ",
			"keywords-account": " parole chiave nel tuo account."
		},
		"label": {
			"keywords": "Parole chiave (una per riga)",
			"language": "Lingua"
		},
		"required": {
			"keyword": "È richiesta almeno una parola chiave"
		},
		"success": "Le parole chiave sono state aggiunte, le posizioni sono in fase di aggiornamento."
	},
	"pt": {
		"add": "A adicionar palavras-chave ...",
		"hint": {
			"keywords": "Introduza uma palavra-chave por linha, pode importar até ",
			"keywords-account": " palavras-chave na sua conta."
		},
		"label": {
			"keywords": "Palavras-chave (uma por linha)",
			"language": "Idioma"
		},
		"required": {
			"keyword": "É necessária pelo menos uma palavra-chave"
		},
		"success": "As palavras-chave foram adicionadas, as posições estão a ser atualizadas."
	},
	"pl": {
		"add": "Dodawanie słów kluczowych ...",
		"hint": {
			"keywords": "Wprowadź jedno słowo kluczowe na linię, możesz zaimportować do ",
			"keywords-account": " słów kluczowych na swoim koncie."
		},
		"label": {
			"keywords": "Słowa kluczowe (jedno na linię)",
			"language": "Język"
		},
		"required": {
			"keyword": "Wymagane jest co najmniej jedno słowo kluczowe"
		},
		"success": "Słowa kluczowe zostały dodane, pozycje są aktualizowane."
	}
}
</i18n>
